import { lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/hooks/AuthProvider";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { NetworkStatus } from "@/components/ui/network-status";
import { FormbricksBridge } from "@/integrations/formbricks/FormbricksBridge";
import * as Sentry from "@sentry/react";
import { RootErrorBoundary } from "@/components/ErrorBoundary";

import AdminLayout from './components/Layout/AdminLayout';
import BALayout from './components/Layout/BALayout';

import {
  LegacyWorkflowRedirect,
  RedirectIfActiveShift,
  RequireActiveShift,
} from '@/components/ba/PromoterWorkflowRoute';
import { LearningProtectedRoute } from '@/components/learning/ProtectedRoute';

const Index = lazy(() => import('./pages/Index'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Auth = lazy(() => import('./pages/Auth'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Apply = lazy(() => import('./pages/Apply'));
const ApplyQuick = lazy(() => import('./pages/ApplyQuick'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const CompleteApplication = lazy(() => import('./pages/ba/CompleteApplication'));
const ProfileView = lazy(() => import('./pages/ba/ProfileView'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const Campaigns = lazy(() => import('./pages/admin/Campaigns'));
const Wages = lazy(() => import('./pages/admin/Wages'));
const WagesVerification = lazy(() => import('./pages/admin/WagesVerification'));
const Messaging = lazy(() => import('./pages/admin/Messaging'));
const CampaignDetail = lazy(() => import('./pages/admin/CampaignDetail'));
const CampaignSurveyResponses = lazy(() => import('./pages/admin/CampaignSurveyResponses'));
const CampaignTraining = lazy(() => import('./pages/admin/CampaignTraining'));
const CampaignTrainingBuilder = lazy(() => import('./pages/admin/CampaignTrainingBuilder'));
const CampaignTrainingResults = lazy(() => import('./pages/admin/CampaignTrainingResults'));
const CampaignTrainingPreview = lazy(() => import('./pages/admin/CampaignTrainingPreview'));
const Banking = lazy(() => import('./pages/admin/Banking'));
const VerifyID = lazy(() => import('./pages/admin/VerifyID'));
const VerificationOverview = lazy(() => import('./pages/admin/VerificationOverview'));
const BudApproval = lazy(() => import('./pages/admin/BudApproval'));
const Stores = lazy(() => import('./pages/admin/Stores'));
const Clients = lazy(() => import('./pages/admin/Clients'));
const ClientDetail = lazy(() => import('./pages/admin/ClientDetail'));
const ClientReports = lazy(() => import('./pages/admin/ClientReports'));
const Promoters = lazy(() => import('./pages/admin/Promoters'));
const Users = lazy(() => import('./pages/admin/Users'));
const PromoterDetail = lazy(() => import('./pages/admin/PromoterDetail'));
const PromoterSegments = lazy(() => import('./pages/admin/PromoterSegments'));
const Applications = lazy(() => import('./pages/admin/Applications'));
const PromoterChanges = lazy(() => import('./pages/admin/PromoterChanges'));
const ContractManagement = lazy(() => import('./pages/admin/ContractManagement'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const Policies = lazy(() => import('./pages/admin/Policies'));
const ReportSheets = lazy(() => import('./pages/admin/ReportSheets'));
const Inventory = lazy(() => import('./pages/admin/Inventory'));
const Surveys = lazy(() => import('./pages/admin/Surveys'));
const SurveyNew = lazy(() => import('./pages/admin/SurveyNew'));
const SurveyTemplateDetail = lazy(() => import('./pages/admin/SurveyTemplateDetail'));
const SurveyVersionEditor = lazy(() => import('./pages/admin/SurveyVersionEditor'));
const SurveyVersionPreview = lazy(() => import('./pages/admin/SurveyVersionPreview'));
const Rollout = lazy(() => import('./pages/admin/Rollout'));
const Training = lazy(() => import('./pages/admin/Training'));
const TrainingNew = lazy(() => import('./pages/admin/TrainingNew'));
const TrainingEdit = lazy(() => import('./pages/admin/TrainingEdit'));
const ClockReports = lazy(() => import('./pages/admin/ClockReports'));
const Profile = lazy(() => import('./pages/admin/Profile'));
const Security = lazy(() => import('./pages/admin/Security'));
const AccessControl = lazy(() => import('./pages/admin/AccessControl'));
const Health = lazy(() => import('./pages/Health'));
const BADashboard = lazy(() => import('./pages/ba/Dashboard'));
const BACampaigns = lazy(() => import('./pages/ba/Campaigns'));
const BACampaignDetail = lazy(() => import('./pages/ba/CampaignDetail'));
const BACampaignSupport = lazy(() => import('./pages/ba/CampaignSupport'));
const BAActiveShift = lazy(() => import('./pages/ba/ActiveShift'));
const BACampaignTraining = lazy(() => import('./pages/ba/CampaignTraining'));
const BACampaignSurveys = lazy(() => import('./pages/ba/CampaignSurveys'));
const BACampaignSurveyRunner = lazy(() => import('./pages/ba/CampaignSurveyRunner'));
const BASurveyPageRoute = lazy(() => import('./pages/ba/BASurveyPage'));
const CampaignScratchSession = lazy(() => import('./pages/ba/CampaignScratchSession'));
const CampaignScratchCheckout = lazy(() => import('./pages/ba/CampaignScratchCheckout'));
const BAMessages = lazy(() => import('./pages/ba/Messages'));
const Notifications = lazy(() => import('./pages/ba/Notifications'));
const BABanking = lazy(() => import('./pages/ba/Banking'));
const Contracts = lazy(() => import('./pages/ba/Contracts'));
const BALogin = lazy(() => import('./pages/ba/Login'));
const BATraining = lazy(() => import('./pages/ba/Training'));
const BABadges = lazy(() => import('./pages/ba/Badges'));
const BABadgeDetail = lazy(() => import('./pages/ba/BadgeDetail'));
const TrainingStart = lazy(() => import('./pages/ba/TrainingStart'));
const TrainingModule = lazy(() => import('./pages/ba/TrainingModule'));
const TrainingAttempt = lazy(() => import('./pages/ba/TrainingAttempt'));
const SurveyQa = lazy(() => import('./pages/dev/SurveyQa'));
const ProductTour = lazy(() => import('./pages/ProductTour'));
const LearningDashboardPage = lazy(() => import('./pages/learning/Dashboard'));
const LearningLoginPage = lazy(() => import('./pages/learning/Login'));
const LearningRegisterPage = lazy(() => import('./pages/learning/Register'));
const LearningCoursesPage = lazy(() => import('./pages/learning/Courses'));
const LearningCourseDetailPage = lazy(() => import('./pages/learning/CourseDetail'));
const LearningCoursePlayPage = lazy(() => import('./pages/learning/CoursePlay'));
const LearningQuizPage = lazy(() => import('./pages/learning/QuizPage'));
const LearningMyBadgesPage = lazy(() => import('./pages/learning/MyBadges'));
const LearningMyCertificatesPage = lazy(() => import('./pages/learning/MyCertificates'));
const LearningProfilePage = lazy(() => import('./pages/learning/Profile'));

const queryClient = new QueryClient();

const ProtectedRoute = ({
  children,
  requireManager,
  requireBrandAmbassador,
  redirectTo = '/',
}: {
  children: React.ReactNode;
  requireManager?: boolean;
  requireBrandAmbassador?: boolean;
  redirectTo?: string;
}) => {
  const { user, loading } = useAuth();
  const { isManager, isBrandAmbassador, isLoading: roleLoading } = useUserRole();
  const location = useLocation();

  if (loading || roleLoading) {
    return <div className="flex h-screen items-center justify-center">Loading...</div>;
  }

  if (!user) {
    const shouldPreserveBaDestination = requireBrandAmbassador && redirectTo === '/ba/login';
    const destination = `${location.pathname}${location.search}${location.hash}`;
    const loginTarget = shouldPreserveBaDestination
      ? `${redirectTo}?returnTo=${encodeURIComponent(destination)}`
      : redirectTo;

    return <Navigate to={loginTarget} replace />;
  }

  if (requireManager && !isManager) {
    return <Navigate to="/ba" replace />;
  }

  if (requireBrandAmbassador && isManager && !isBrandAmbassador) {
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
};

const AppRoutes = () => {
  const { user } = useAuth();
  const { isManager, isLoading } = useUserRole();

  if (isLoading && user) {
    return <div className="flex h-screen items-center justify-center">Loading...</div>;
  }

  return (
    <Routes>
      <Route path="/auth" element={<Auth />} />
      <Route path="/ba/login" element={<BALogin />} />
      <Route path="/promoter/login" element={<BALogin />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/apply-old" element={<Apply />} />
      <Route path="/apply" element={<ApplyQuick />} />
      <Route path="/health" element={<Health />} />
      <Route path="/product-tour" element={<ProductTour />} />
      {import.meta.env.DEV && <Route path="/__qa/surveys" element={<SurveyQa />} />}
      <Route
        path="/"
        element={
          user ? (
            <Navigate to={isManager ? '/admin' : '/ba'} replace />
          ) : (
            <Index />
          )
        }
      />

      {/* Learning Routes */}
      <Route path="/login" element={<LearningLoginPage />} />
      <Route path="/register" element={<LearningRegisterPage />} />
      <Route path="/courses" element={<LearningCoursesPage />} />
      <Route path="/courses/:id" element={<LearningCourseDetailPage />} />
      <Route
        path="/learning"
        element={
          <LearningProtectedRoute>
            <LearningDashboardPage />
          </LearningProtectedRoute>
        }
      />
      <Route
        path="/learn/:enrollmentId"
        element={
          <LearningProtectedRoute>
            <LearningCoursePlayPage />
          </LearningProtectedRoute>
        }
      />
      <Route
        path="/quiz/:assessmentId"
        element={
          <LearningProtectedRoute>
            <LearningQuizPage />
          </LearningProtectedRoute>
        }
      />
      <Route
        path="/my-badges"
        element={
          <LearningProtectedRoute>
            <LearningMyBadgesPage />
          </LearningProtectedRoute>
        }
      />
      <Route
        path="/my-certificates"
        element={
          <LearningProtectedRoute>
            <LearningMyCertificatesPage />
          </LearningProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <LearningProtectedRoute>
            <LearningProfilePage />
          </LearningProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <AdminDashboard />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/campaigns"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Campaigns />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/campaigns/:id"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <CampaignDetail />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/campaigns/:id/:workspace"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <CampaignDetail />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/campaigns/:id/responses"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <CampaignSurveyResponses />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/campaigns/:id/training"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <CampaignTraining />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/campaigns/:id/training/builder/:moduleId"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <CampaignTrainingBuilder />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/campaigns/:id/training/results"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <CampaignTrainingResults />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/campaigns/:id/training/preview/:moduleId"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <CampaignTrainingPreview />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/assignments"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <Navigate to="/admin/campaigns" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/wages"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Wages />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inventory"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Inventory />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/wages-verification"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <WagesVerification />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/messaging"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Messaging />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/stores"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Stores />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/banking"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Banking />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/verify-id"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <VerifyID />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/verification-overview"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <VerificationOverview />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/bud-approval"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <BudApproval />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Clients />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients/:id"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <ClientDetail />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients/:id/reports"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <ClientReports />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Users />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/promoters"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Promoters />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/promoters/segments"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <PromoterSegments />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/promoters/:id"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <PromoterDetail />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/applications"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Applications />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/promoter-changes"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <PromoterChanges />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/contracts"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <ContractManagement />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Settings />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/policies"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Policies />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/report-sheets"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <ReportSheets />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/surveys"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Surveys />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/surveys/new"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <SurveyNew />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/surveys/:templateId"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <SurveyTemplateDetail />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/surveys/:templateId/versions/:versionId/edit"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <SurveyVersionEditor />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/surveys/:templateId/versions/:versionId/preview"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <SurveyVersionPreview />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/rollout"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Rollout />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/training"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Training />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/training/new"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <TrainingNew />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/training/:id/edit"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <TrainingEdit />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clock-reports"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <ClockReports />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/profile"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Profile />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/security"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <Security />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/access-control"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <AccessControl />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requireManager redirectTo="/auth">
            <AdminLayout>
              <div className="text-center py-12">
                <h2 className="text-2xl font-bold mb-2">Coming Soon</h2>
                <p className="text-muted-foreground">This page is under development</p>
              </div>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      {/* BA Routes */}
      <Route
        path="/ba"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <Navigate to="/ba/home" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/dashboard"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <Navigate to="/ba/home" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/home"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <BALayout>
              <BADashboard />
            </BALayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/surveys"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <LegacyWorkflowRedirect
              whenActive={() => '/ba/active-shift/surveys'}
              whenInactive={() => '/ba/campaigns'}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/complete-application"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <CompleteApplication />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/profile"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <BALayout>
              <ProfileView />
            </BALayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/profile/view"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <Navigate to="/ba/profile" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/campaigns"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <RedirectIfActiveShift>
              <BALayout>
                <BACampaigns />
              </BALayout>
            </RedirectIfActiveShift>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/campaigns/:id"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <RedirectIfActiveShift>
              <BALayout>
                <BACampaignDetail />
              </BALayout>
            </RedirectIfActiveShift>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/campaigns/:id/support"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <BALayout>
              <BACampaignSupport />
            </BALayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/campaigns/:campaignId/surveys"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <LegacyWorkflowRedirect
              whenActive={() => '/ba/active-shift/surveys'}
              whenInactive={({ campaignId }) =>
                campaignId ? `/ba/campaigns/${campaignId}` : '/ba/campaigns'
              }
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/campaigns/:campaignId/surveys/:campaignReportSheetId"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <LegacyWorkflowRedirect
              whenActive={({ campaignReportSheetId }) =>
                campaignReportSheetId
                  ? `/ba/active-shift/surveys/${campaignReportSheetId}`
                  : '/ba/active-shift/surveys'
              }
              whenInactive={({ campaignId }) =>
                campaignId ? `/ba/campaigns/${campaignId}` : '/ba/campaigns'
              }
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/campaigns/:campaignId/scratch"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <LegacyWorkflowRedirect
              whenActive={() => '/ba/active-shift/scratch'}
              whenInactive={({ campaignId }) =>
                campaignId ? `/ba/campaigns/${campaignId}` : '/ba/campaigns'
              }
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/campaigns/:campaignId/scratch/checkout"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <LegacyWorkflowRedirect
              whenActive={() => '/ba/active-shift/scratch/checkout'}
              whenInactive={({ campaignId }) =>
                campaignId ? `/ba/campaigns/${campaignId}` : '/ba/campaigns'
              }
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/active-shift"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <RequireActiveShift>
              <BALayout>
                <BAActiveShift />
              </BALayout>
            </RequireActiveShift>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/active-shift/surveys"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <RequireActiveShift>
              <BALayout>
                <BACampaignSurveys />
              </BALayout>
            </RequireActiveShift>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/active-shift/surveys/:campaignReportSheetId"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <RequireActiveShift>
              <BALayout>
                <BACampaignSurveyRunner />
              </BALayout>
            </RequireActiveShift>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/active-shift/surveys/:surveyId/fill"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <RequireActiveShift>
              <BALayout>
                <BASurveyPageRoute />
              </BALayout>
            </RequireActiveShift>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/checkout-sessions/:checkinSessionId/surveys/:campaignReportSheetId"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <BALayout>
              <BACampaignSurveyRunner />
            </BALayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/active-shift/scratch"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <RequireActiveShift>
              <BALayout>
                <CampaignScratchSession />
              </BALayout>
            </RequireActiveShift>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/active-shift/scratch/checkout"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <RequireActiveShift>
              <BALayout>
                <CampaignScratchCheckout />
              </BALayout>
            </RequireActiveShift>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/campaigns/:id/training"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <RedirectIfActiveShift>
              <BALayout>
                <BACampaignTraining />
              </BALayout>
            </RedirectIfActiveShift>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/trainings/:trainingId/start"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <RedirectIfActiveShift>
              <BALayout>
                <TrainingStart />
              </BALayout>
            </RedirectIfActiveShift>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/trainings/:trainingId"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <RedirectIfActiveShift>
              <BALayout>
                <TrainingModule />
              </BALayout>
            </RedirectIfActiveShift>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/trainings/:trainingId/attempts/:attemptId"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <RedirectIfActiveShift>
              <BALayout>
                <TrainingAttempt />
              </BALayout>
            </RedirectIfActiveShift>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/training"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <RedirectIfActiveShift>
              <BALayout>
                <BATraining />
              </BALayout>
            </RedirectIfActiveShift>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/badges"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <BALayout>
              <BABadges />
            </BALayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/badges/:id"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <BALayout>
              <BABadgeDetail />
            </BALayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/messages"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <BALayout>
              <BAMessages />
            </BALayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/invites"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <LegacyWorkflowRedirect
              whenActive={() => '/ba/active-shift'}
              whenInactive={() => '/ba/campaigns'}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/shifts"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <LegacyWorkflowRedirect
              whenActive={() => '/ba/active-shift'}
              whenInactive={() => '/ba/campaigns'}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/notifications"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <BALayout>
              <Notifications />
            </BALayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/banking"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <BALayout>
              <BABanking />
            </BALayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/contracts"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <RedirectIfActiveShift>
              <BALayout>
                <Contracts />
              </BALayout>
            </RedirectIfActiveShift>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/standby"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <LegacyWorkflowRedirect
              whenActive={() => '/ba/active-shift'}
              whenInactive={() => '/ba/campaigns'}
            />
          </ProtectedRoute>
        }
      />

      {/* Legacy BA portal aliases */}
      <Route
        path="/portal/invites"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <LegacyWorkflowRedirect
              whenActive={() => '/ba/active-shift'}
              whenInactive={() => '/ba/campaigns'}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/shifts"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <LegacyWorkflowRedirect
              whenActive={() => '/ba/active-shift'}
              whenInactive={() => '/ba/campaigns'}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/surveys"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <LegacyWorkflowRedirect
              whenActive={() => '/ba/active-shift/surveys'}
              whenInactive={() => '/ba/campaigns'}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/contracts"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <Navigate to="/ba/contracts" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/banking"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <Navigate to="/ba/banking" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/notifications"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <Navigate to="/ba/notifications" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/standby"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <LegacyWorkflowRedirect
              whenActive={() => '/ba/active-shift'}
              whenInactive={() => '/ba/campaigns'}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ba/contract"
        element={<Navigate to="/ba/contracts" replace />}
      />
      <Route
        path="/ba/*"
        element={
          <ProtectedRoute requireBrandAmbassador redirectTo="/ba/login">
            <BALayout>
              <div className="text-center py-12">
                <h2 className="text-2xl font-bold mb-2">Coming Soon</h2>
                <p className="text-muted-foreground">This page is under development</p>
              </div>
            </BALayout>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <Sentry.ErrorBoundary 
    fallback={({ error }) => <RootErrorBoundary error={error} />} 
    showDialog={false}
  >
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <FormbricksBridge />
            <Suspense fallback={
              <div role="status" className="flex min-h-screen items-center justify-center">
                Loading page…
              </div>
            }>
              <AppRoutes />
            </Suspense>
            <NetworkStatus />
          </TooltipProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

const AppWithProfiler = Sentry.withProfiler(App);

export default AppWithProfiler;
